import { useNode } from "@craftjs/core";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import useApplyGlobalSettings, {
  CODEBLOCK_TYPES,
} from "../../../../hooks/useApplyGlobalSettings";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { toggleSegmentSettings } from "../../../../redux/actions/segmentSettings";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import { goToUrl } from "../../../../utils/helpers";
import { autoTextColor } from "../../../../utils/contrast";
import { sanitizeUserHtml } from "../../../../utils/sanitizeHtml";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import { ButtonSettingsComp } from "./Settings";

export const Button = ({
  backgroundColor,
  radius,
  fullWidth,
  margin,
  allSideMargin,
  opacity,
  border,
  dropShadowColor,
  buttonAlignment,
  buttonTitle,
  buttonLink,
  allButtons,
  ...props
}) => {
  const {
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode();
  const selectedSegment = useSelector(getSelectedSegment);
  const { buttonProps } = useSelector((state) => state.codeblockStyles.button);
  const selectedSegmentColor = selectedSegment?.settings?.color;
  const { getPaletteColor } = usePaletteColor();
  const location = useLocation();

  const isEditPage = location.pathname.includes("builder");
  useEffect(() => {
    if (allButtons?.length) {
      setProp((props) => {
        props.allButtons = props.allButtons.map((button) => {
          // Only apply styles if button is active AND has null styles
          if (
            button &&
            button.isActive &&
            (!button.border.color ||
              button.border.width === null ||
              button.border.width === undefined ||
              !button.backgroundColor ||
              button.radius === null ||
              !button.dropShadowColor ||
              !button.borderColor)
          ) {
            if (!button.border.color) {
              button.border.color =
                buttonProps?.border?.color || "suggested_color_0";
            }
            if (
              button.border.width === null ||
              button.border.width === undefined
            ) {
              button.border.width = buttonProps?.border?.width || 1;
            }
            if (!button.backgroundColor) {
              button.backgroundColor =
                buttonProps?.backgroundColor || "suggested_color_0";
            }
            if (button?.radius === null) {
              button.radius = buttonProps?.radius || 8;
            }
            if (
              button.dropShadowColor === null ||
              button.dropShadowColor === undefined
            ) {
              button.dropShadowColor =
                buttonProps?.dropShadowColor || "suggested_color_0";
            }
            if (!button.borderColor) {
              button.borderColor =
                buttonProps?.borderColor || "suggested_color_0";
            }
          }
          return button;
        });
      });
    }
  }, [allButtons, buttonProps]);

  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.Buttons);

  const breakLongWordsInHTML = (html, maxLength) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const breakLongWords = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const words = node.textContent.split(" ");
        const brokenText = words
          .map((word) => {
            if (word.length > maxLength) {
              return word
                .match(new RegExp(".{1," + maxLength + "}", "g"))
                .join("­"); // Adding soft hyphen
            }
            return word;
          })
          .join(" ");
        node.textContent = brokenText;
      } else {
        node.childNodes.forEach(breakLongWords);
      }
    };

    doc.body.childNodes.forEach(breakLongWords);

    return doc.body.innerHTML;
  };

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent:
              buttonAlignment === "left"
                ? "flex-start"
                : buttonAlignment === "right"
                ? "flex-end"
                : "center",
            paddingLeft: margin?.left ?? 0,
            paddingRight: margin?.right ?? 0,
            paddingTop:margin?.top ?? 0,
            paddingBottom:margin?.bottom ?? 0,
          }}
        >
          {allButtons.map(
            (item) =>
              item.isActive && (
                <div
                  key={item.id}
                  {...props}
                  style={{
                    flex: fullWidth ? 1 : "initial",
                    margin: "5px",
                    backgroundColor: getPaletteColor(item?.backgroundColor),
                    // D-11: default button text auto-derives light/dark from the
                    // rendered fill so a dark background never yields illegible
                    // black text. A manual color set in the title rich-text
                    // editor (textColorCustom) is inline and still wins; a null
                    // (unsampleable/image bg) is ignored by React, preserving
                    // the prior default.
                    color: autoTextColor(getPaletteColor(item.backgroundColor)),
                    boxSizing: "border-box",
                    borderRadius: `${item.radius}px`,
                    borderColor: getPaletteColor(item.border?.color),
                    borderWidth: `${item.border?.width}px`,
                    borderStyle: "solid",
                    boxShadow: item.dropShadowColor
                      ? `0px 4px 12px ${getPaletteColor(item.dropShadowColor)}`
                      : "none",
                    backgroundImage: item?.backgroundColor?.startsWith(
                      "linear-gradient"
                    )
                      ? getPaletteColor(item.backgroundColor)
                      : "none",
                    wordBreak: "break-word",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "10px",
                    textAlign: "center",
                  }}
                  ref={(ref) => connect(drag(ref))}
                >
                  <div
                    onClick={() => {
                      if (!isEditPage && item?.link) {
                        // Check if the link contains HTML
                        const containsHTML = /<\/?[^>]+(>|$)/g.test(item.link);

                        // Replace HTML if it exists, otherwise use the link as is
                        let sanitizedLink = containsHTML
                          ? item.link.replace(/<\/?[^>]+(>|$)/g, "")
                          : item.link;

                        // Remove &amp; from the link
                        sanitizedLink = sanitizedLink.replace(/&amp;/g, "&");

                        goToUrl(sanitizedLink);
                      }
                    }}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginBottom: /<\/?[a-z][\s\S]*>/i.test(item.title)
                        ? "-16px"
                        : "none",
                      cursor: "pointer",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeUserHtml(
                        breakLongWordsInHTML(item.title, 10)
                      ),
                    }}
                  ></div>
                </div>
              )
          )}
        </div>
      </BlockIndicator>
    </div>
  );
};

export const ButtonSettings = () => {
  const {
    backgroundColor,
    radius,
    fullWidth,
    margin,
    allSideMargin,
    opacity,
    border,
    dropShadowColor,
    buttonAlignment,
    buttonTitle,
    buttonLink,
    allButtons,
    actions: { setProp },
  } = useNode((node) => ({
    backgroundColor: node.data.props.backgroundColor,
    radius: node.data.props.radius,
    fullWidth: node.data.props.fullWidth,
    margin: node.data.props.margin,
    allSideMargin: node.data.props.allSideMargin,
    opacity: node.data.props.opacity,
    border: node.data.props.border,
    dropShadowColor: node.data.dropShadowColor,
    // Buttons
    buttonAlignment: node.data.props.buttonAlignment,
    buttonTitle: node.data.props.buttonTitle,
    buttonLink: node.data.props.buttonLink,
    allButtons: node.data.props.allButtons,
  }));
  const dispatch = useDispatch();

  const handleChange = (
    type,
    value,
    buttonIndex = null,
    sideOrValue = null
  ) => {
    dispatch(toggleSegmentSettings(false));

    setProp((props) => {
      if (buttonIndex !== null) {
        const button = props.allButtons[buttonIndex];
        switch (type) {
          case "radius":
            button.radius = value;
            break;
          case "addColor":
            button.backgroundColor = value;
            break;
          case "resetToDefault":
            button.backgroundColor = "#ffffff";
            break;
          case "transparency":
            button.opacity = value;
            break;
          case "borderChange":
            button.border[sideOrValue] = value;
            break;
          case "resetBorderColor":
            button.border.color = "#ffffff";
            break;
          case "dropShadowColor":
            button.dropShadowColor = value;
            break;
          case "resetShadow":
            button.dropShadowColor = null;
            break;
          case "changeButtonTitle":
            button.title = value;
            break;
          case "changeButtonLink":
            button.link = value;
            break;
          case "toggleActive":
            button.isActive = !button.isActive;
            break;
          default:
            break;
        }
      } else {
        switch (type) {
          case "fullWidth":
            if (value !== "full") {
              props.margin.top = 10;
              props.margin.left = 20;
              props.margin.right = 20;
            } else {
              props.margin.top = 0;
              props.margin.left = 0;
              props.margin.right = 0;
            }
            props.fullWidth = value === "full";
            break;

          case "margin":
            if (value >= 0 && value <= 100) {
              props.margin[sideOrValue] = value;
            }
            break;
          case "marginChange":
            props.allSideMargin = value;
            break;
          case "changeAlignment":
            props.buttonAlignment = value;
            break;

          case "changeButtonList":
            props.allButtons = value;
            break;
          default:
            break;
        }
      }
    });
  };

  return (
    <ButtonSettingsComp
      setProp={setProp}
      background={backgroundColor}
      radius={radius}
      fullWidth={fullWidth}
      handleWidthChange={(value) => handleChange("fullWidth", value)}
      handleBorderRadius={(value) => handleChange("radius", value)}
      handleMarginChange={(side, value) =>
        handleChange("margin", value, null, side)
      }
      margin={margin}
      handleMarginCheckBox={() => handleChange("marginToggle")}
      allSideMargin={allSideMargin}
      handleSliderMarginChange={(value) => handleChange("marginChange", value)}
      handleChangeBackgroundColor={(value) => handleChange("addColor", value)}
      resetBackgroundColor={() => handleChange("resetToDefault")}
      transparency={opacity}
      handleTransparencyChange={(value) => handleChange("transparency", value)}
      borderSelectedColor={border?.color}
      handleBorderChange={(side, value) =>
        handleChange("borderChange", value, null, side)
      }
      borderWidth={border?.width}
      resetBorderColor={() => handleChange("resetBorderColor")}
      dropShadowColor={dropShadowColor}
      handleDropShadowChange={(val) => handleChange("dropShadowColor", val)}
      handleDropShadowReset={() => handleChange("resetShadow")}
      // Buttons
      selectedAlignment={buttonAlignment}
      handleAlignmentChange={(value) => handleChange("changeAlignment", value)}
      buttonTitle={buttonTitle}
      buttonLink={buttonLink}
      handleTitleChange={(value) => handleChange("changeButtonTitle", value)}
      handleLinkChange={(value) => handleChange("changeButtonLink", value)}
      allButtons={allButtons.map((button, index) => ({
        ...button,
        handleRadiusChange: (value) => handleChange("radius", value, index),
        handleChangeBackgroundColor: (value) =>
          handleChange("addColor", value, index),
        resetBackgroundColor: () => handleChange("resetToDefault", null, index),
        handleBorderChange: (side, value) =>
          handleChange("borderChange", value, index, side),
        resetBorderColor: () => handleChange("resetBorderColor", null, index),
        handleDropShadowChange: (value) =>
          handleChange("dropShadowColor", value, index),
        handleDropShadowReset: () => handleChange("resetShadow", null, index),
        handleTitleChange: (value) =>
          handleChange("changeButtonTitle", value, index),
        handleLinkChange: (value) =>
          handleChange("changeButtonLink", value, index),
        handleToggleActive: () => handleChange("toggleActive", null, index),
      }))}
      handleChangeButtonsList={(list) => handleChange("changeButtonList", list)}
    />
  );
};

export const ButtonDefaultProps = {
  buttonAlignment: "center",
  buttonTitle: "",
  buttonLink: "",
  radius: 0,
  fullWidth: false,

  margin: {
    top: 5,
    right: 10,
    bottom: 5,
    left: 10,
  },
  allSideMargin: 5,
  opacity: 100,
  border: {
    color: "#ffffff",
    width: 0,
  },
  allButtons: [
    {
      id: 1,
      title: "Button 1",
      isActive: true,
      radius: 8,
      link: "",
      backgroundColor: null,
      opacity: 100,
      border: { color: null, width: null },
      dropShadowColor: null,
    },
    {
      id: 2,
      title: "Button 2",
      isActive: false,
      radius: null,
      link: "",
      backgroundColor: null,
      opacity: 100,
      border: { color: null, width: null },
      dropShadowColor: null,
    },
    {
      id: 3,
      title: "Button 3",
      isActive: false,
      radius: null,
      link: "",
      backgroundColor: null,
      opacity: 100,
      border: { color: null, width: null },
      dropShadowColor: null,
    },
  ],
};

Button.craft = {
  props: ButtonDefaultProps,
  related: {
    settings: ButtonSettings,
  },
};
